import { repApi, qs } from './repApi';
import type {
  Capability,
  Eopa,
  EopaDocument,
  EopaNote,
  Exercise,
  ExerciseBody,
  ExerciseCapability,
  ExerciseMilestone,
  MilestoneItem,
  Plant,
  Requirement,
  SelectedCapability,
} from '@/types';

export const exercisesApi = repApi.injectEndpoints({
  endpoints: (builder) => ({
    requirements: builder.query<Requirement[], void>({
      query: () => '/requirements',
      keepUnusedDataFor: 3600,
    }),
    plants: builder.query<Plant[], void>({
      query: () => '/plants',
      keepUnusedDataFor: 3600,
      providesTags: ['Plants'],
    }),
    getExercise: builder.query<Exercise, number>({
      query: (id) => `/exercises/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Exercise', id }],
    }),
    createExercise: builder.mutation<Exercise, ExerciseBody>({
      query: (body) => ({ url: '/exercises', method: 'POST', body }),
      invalidatesTags: ['Exercise'],
    }),
    updateExercise: builder.mutation<Exercise, { id: number; body: Partial<ExerciseBody> }>({
      query: ({ id, body }) => ({ url: `/exercises/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Exercise', id }, 'Exercise'],
    }),
    deleteExercise: builder.mutation<{ pvDataID: number }, number>({
      query: (id) => ({ url: `/exercises/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Exercise'],
    }),
    updateExerciseCapabilities: builder.mutation<
      ExerciseCapability[],
      { id: number; selectedCapabilities: SelectedCapability[] }
    >({
      query: ({ id, selectedCapabilities }) => ({
        url: `/exercises/${id}/capabilities`,
        method: 'PUT',
        body: { selectedCapabilities },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Exercise', id }],
    }),
    updateEopa: builder.mutation<
      { EOPA: Eopa; notes: EopaNote[] },
      {
        id: number;
        pvStateEOP?: EopaDocument | null;
        pvRiskEOP?: EopaDocument | null;
        pvHostEOP?: EopaDocument | null;
        resetReviewed?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({ url: `/exercises/${id}/eopa`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Exercise', id }],
    }),
    eopaNotes: builder.query<EopaNote[], { id: number; status?: string }>({
      query: ({ id, status }) => `/exercises/${id}/eopa/notes${qs({ status })}`,
      providesTags: (_r, _e, { id }) => [{ type: 'Exercise', id: `notes-${id}` }],
    }),
    addEopaNote: builder.mutation<
      EopaNote[],
      {
        id: number;
        pvActionType: 'info' | 'comments' | 'completed';
        pvStatus?: string;
        pvTitle?: string;
        pvSubTitle?: string;
        pvCounty?: string;
        pvSectionNoteField?: string;
        pvNotes?: string;
      }
    >({
      query: ({ id, ...body }) => ({ url: `/exercises/${id}/eopa/notes`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Exercise', id: `notes-${id}` }],
    }),
    updateExerciseMilestone: builder.mutation<
      ExerciseMilestone[],
      {
        id: number;
        milestoneId: number;
        pvDueDate?: string;
        pvTotalDays?: number;
        pvItems?: MilestoneItem[];
      }
    >({
      query: ({ id, milestoneId, ...body }) => ({
        url: `/exercises/${id}/milestones/${milestoneId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Exercise', id }],
    }),
    // Evaluation areas (capabilities per plant)
    listCapabilities: builder.query<Capability[], { plantId?: number } | void>({
      query: (arg) => `/capabilities${qs({ plantId: arg ? arg.plantId : undefined })}`,
      providesTags: ['Capability'],
    }),
    getCapability: builder.query<Capability, number>({
      query: (id) => `/capabilities/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Capability', id }],
    }),
    createCapability: builder.mutation<Capability, Partial<Capability>>({
      query: (body) => ({ url: '/capabilities', method: 'POST', body }),
      invalidatesTags: ['Capability'],
    }),
    updateCapability: builder.mutation<Capability, { id: number; body: Partial<Capability> }>({
      query: ({ id, body }) => ({ url: `/capabilities/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => ['Capability', { type: 'Capability', id }],
    }),
    deleteCapability: builder.mutation<{ pvDataID: number }, number>({
      query: (id) => ({ url: `/capabilities/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Capability'],
    }),
  }),
});

export const {
  useRequirementsQuery,
  usePlantsQuery,
  useGetExerciseQuery,
  useCreateExerciseMutation,
  useUpdateExerciseMutation,
  useDeleteExerciseMutation,
  useUpdateExerciseCapabilitiesMutation,
  useUpdateEopaMutation,
  useEopaNotesQuery,
  useAddEopaNoteMutation,
  useUpdateExerciseMilestoneMutation,
  useListCapabilitiesQuery,
  useGetCapabilityQuery,
  useCreateCapabilityMutation,
  useUpdateCapabilityMutation,
  useDeleteCapabilityMutation,
} = exercisesApi;
